// ./src/components/generic/MediaSelector/tabs/ExternalUrlTab.tsx
//
// "External URL" — paste a link to media hosted elsewhere (a direct image/
// video/audio file, or a YouTube/Vimeo link for video). The URL is validated to
// actually resolve to the requested kind before it can be selected, then a small
// preview is shown. Selecting yields a URL result (no uploaded_file_id).

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Stack, TextInput, Button, Group, Text, Box, Alert } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { AppIcon } from "../../AppIcon";
import { MediaKind } from "../mediaSelectorConfig";
import { validateMediaUrl } from "../validateMediaUrl";

type Status = "idle" | "checking" | "valid" | "invalid";

export interface ExternalUrlTabProps {
  kind: MediaKind;
  /**
   * The text in the box — held by the picker, not here (#124): a URL still being typed is work the picker must ask
   * about before closing, and the tabs unmount, so text kept here was also lost on a tab switch.
   */
  url: string;
  onUrlChange: (url: string) => void;
  /**
   * Check the box's text when the tab mounts. False when something else is already picked (a library file chosen after
   * typing a URL): looking at this tab again must not swap that pick for the URL.
   */
  checkOnMount: boolean;
  /** Called with the validated URL, or null when cleared/invalid. */
  onPick: (url: string | null) => void;
}

export const ExternalUrlTab: React.FC<ExternalUrlTabProps> = ({ kind, url, onUrlChange, checkOnMount, onPick }) => {
  const { t } = useTranslation(["common"]);
  // The latest check: an answer for an older one — the text changed since, or the tab unmounted (the picker closed or
  // moved on) — is dropped, so a slow check never becomes the pick after the picker was reset.
  const checkRunRef = useRef(0);
  const [status, setStatus] = useState<Status>("idle");
  const [reason, setReason] = useState<string | null>(null);
  const [isEmbed, setIsEmbed] = useState(false);

  const check = useCallback(
    async (value: string) => {
      const run = ++checkRunRef.current;
      const trimmed = value.trim();
      if (!trimmed) {
        setStatus("idle");
        setReason(null);
        onPick(null);
        return;
      }

      setStatus("checking");
      setReason(null);
      onPick(null);

      const result = await validateMediaUrl(trimmed, kind);

      if (run !== checkRunRef.current) {
        return;
      }

      if (result.ok) {
        setIsEmbed(Boolean(result.isEmbed));
        setStatus("valid");
        onPick(trimmed);
      } else {
        setStatus("invalid");
        setReason(result.reason ?? null);
      }
    },
    [kind, onPick]
  );

  // Validate what the box holds on mount — the current value, or a URL typed before a tab switch.
  useEffect(() => {
    if (checkOnMount && url.trim()) {
      check(url);
    }

    return () => {
      checkRunRef.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (value: string) => {
    checkRunRef.current += 1;
    onUrlChange(value);
    setStatus("idle");
    setReason(null);
    onPick(null);
  };

  return (
    <Stack gap="sm">
      <Group gap="xs" align="flex-end" wrap="nowrap">
        <TextInput
          style={{ flex: 1 }}
          label={t("common:mediaSelector.urlLabel", { defaultValue: "Media URL" })}
          placeholder={
            kind === "video"
              ? "https://… (direct video, YouTube or Vimeo)"
              : kind === "audio"
                ? "https://… (direct audio file)"
                : "https://… (direct image)"
          }
          value={url}
          onChange={(e) => handleChange(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              check(url);
            }
          }}
          error={status === "invalid" ? reason : undefined}
        />
        <Button
          size="md"
          variant="light"
          loading={status === "checking"}
          disabled={!url.trim()}
          onClick={() => check(url)}
        >
          {t("common:mediaSelector.checkUrl", { defaultValue: "Check" })}
        </Button>
      </Group>

      {status === "valid" && (
        <Box>
          <Group gap={6} mb="xs">
            <AppIcon iconName="Check" size={16} style={{ color: "var(--mantine-color-green-6)" }} />
            <Text size="md" c="green.7">
              {t("common:mediaSelector.urlValid", { defaultValue: "Looks good — this will be used." })}
            </Text>
          </Group>

          {kind === "image" && (
            <img
              src={url}
              alt=""
              style={{ maxWidth: "100%", maxHeight: 220, borderRadius: 8, objectFit: "contain" }}
            />
          )}

          {kind === "video" && !isEmbed && (
            <video src={url} controls style={{ maxWidth: "100%", maxHeight: 220, borderRadius: 8 }} />
          )}

          {kind === "video" && isEmbed && (
            <Alert color="blue" variant="light" icon={<AppIcon iconName="PlayerPlay" size={18} />}>
              {t("common:mediaSelector.embedNote", {
                defaultValue: "Video link recognised — it'll be embedded on the page.",
              })}
            </Alert>
          )}

          {kind === "audio" && <audio src={url} controls style={{ width: "100%" }} />}
        </Box>
      )}
    </Stack>
  );
};
